import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import { inactivityApi } from '../services/api';
import { apiErrorText } from '../utils/apiError';
import { Notice } from './Notice';
import type {
  InactivityCase,
  Milestone,
  MilestoneBankIdSessionResponse
} from '../types';

type Props = {
  milestone: Milestone;
  cases: InactivityCase[];
  viewerSide?: 'person' | 'company' | null;
  onResolved?: () => Promise<void> | void;
  /** Opens the stage, for the party whose answer is awaited. */
  onOpenMilestone?: (milestone: Milestone) => void;
  /** Which contract and stage the case is about, above the notice's title. */
  context?: React.ReactNode;
};

/**
 * Whether the viewer has to act in a silence case (ClickUp 869fjrytd): the
 * silent party answers, and once the deadline has passed the waiting party may
 * close it. Actions go under "Nästa steg", the rest stays with the status.
 */
export function inactivityViewerActs(lifecycleCase: InactivityCase, viewerSide: 'person' | 'company' | null) {
  const silentSide = lifecycleCase.caseType === 'seller_silence' ? 'company' : 'person';
  const resolverSide = lifecycleCase.caseType === 'seller_silence' ? 'person' : 'company';
  const resolutionAvailable = lifecycleCase.context.resolutionAvailable === true;
  const canResolve = lifecycleCase.status === 'settling' && resolutionAvailable &&
    typeof lifecycleCase.context.resolutionConfirmedAt !== 'string' && viewerSide === resolverSide;
  return canResolve || (viewerSide === silentSide && !resolutionAvailable);
}

type ResolutionStatus =
  | 'idle'
  | 'starting'
  | 'waiting'
  | 'saving'
  | 'success'
  | 'cancelled'
  | 'failed';

const dateTime = (value: string) =>
  new Date(value).toLocaleString('sv-SE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });

const wait = (milliseconds: number) =>
  new Promise(resolve => window.setTimeout(resolve, milliseconds));

export function InactivityCasePanel({
  milestone,
  cases,
  viewerSide = null,
  onResolved,
  onOpenMilestone,
  context
}: Props) {
  const { t } = useTranslation();
  const relevant = useMemo(
    () => cases.filter(item =>
      item.milestoneId === milestone.id &&
      ['seller_silence', 'buyer_silence'].includes(item.caseType)
    ),
    [cases, milestone.id]
  );
  const active = relevant.find(item => ['active', 'settling'].includes(item.status));
  const completed = relevant.find(item => item.status === 'completed');
  const displayed = active ?? completed;
  const [showResolution, setShowResolution] = useState(false);
  const [resolutionStatus, setResolutionStatus] = useState<ResolutionStatus>('idle');
  const [bankIdSession, setBankIdSession] = useState<MilestoneBankIdSessionResponse | null>(null);
  const [bankIdQrImage, setBankIdQrImage] = useState<string | null>(null);
  const [resolutionError, setResolutionError] = useState<string | null>(null);
  const resolutionRunRef = useRef(0);

  useEffect(() => () => {
    ++resolutionRunRef.current;
  }, []);

  useEffect(() => {
    const qrData = bankIdSession?.qrCode?.data;
    if (!qrData) {
      setBankIdQrImage(null);
      return;
    }
    let mounted = true;
    QRCode.toDataURL(qrData, { width: 192, margin: 1 })
      .then(image => {
        if (mounted) setBankIdQrImage(image);
      })
      .catch(() => {
        if (mounted) setBankIdQrImage(null);
      });
    return () => {
      mounted = false;
    };
  }, [bankIdSession?.qrCode?.data]);

  if (!displayed) return null;

  const silentParty = displayed.caseType === 'seller_silence'
    ? t('inactivityCase.supplierParty')
    : t('inactivityCase.buyerParty');
  const capitalizedSilentParty = silentParty
    ? `${silentParty[0].toUpperCase()}${silentParty.slice(1)}`
    : silentParty;
  const counterparty = displayed.caseType === 'seller_silence'
    ? t('inactivityCase.buyerParty')
    : t('inactivityCase.supplierParty');
  const silentSide = displayed.caseType === 'seller_silence'
    ? 'company'
    : 'person';
  const requestedAction = displayed.caseType === 'seller_silence'
    ? t('inactivityCase.requestedActionSeller')
    : t('inactivityCase.requestedActionBuyer');
  const directPrompt = displayed.caseType === 'seller_silence'
    ? t('inactivityCase.directPromptSeller')
    : t('inactivityCase.directPromptBuyer');
  const reminderPrompt = displayed.caseType === 'seller_silence'
    ? t('inactivityCase.reminderPromptSeller')
    : t('inactivityCase.reminderPromptBuyer');
  const finalConsequence = displayed.caseType === 'seller_silence'
    ? t('inactivityCase.finalConsequenceSeller')
    : t('inactivityCase.finalConsequenceBuyer');
  const expectedResolverSide = displayed.caseType === 'seller_silence'
    ? 'person'
    : 'company';
  const isSilentViewer = viewerSide === silentSide;
  const resolutionAvailable = active?.context.resolutionAvailable === true;
  const resolutionConfirmed = typeof active?.context.resolutionConfirmedAt === 'string';
  const canResolve = Boolean(
    active?.status === 'settling' &&
    resolutionAvailable &&
    !resolutionConfirmed &&
    viewerSide === expectedResolverSide
  );

  const handleSession = async (
    session: MilestoneBankIdSessionResponse,
    runId: number
  ) => {
    if (resolutionRunRef.current !== runId || !active) return true;
    setBankIdSession(session);
    if (session.status === 'complete' || session.verified) {
      setResolutionStatus('saving');
      try {
        await inactivityApi.resolve(active.id, session.verificationRef);
        if (resolutionRunRef.current === runId) {
          setResolutionStatus('success');
          await onResolved?.();
        }
      } catch (caught: any) {
        if (resolutionRunRef.current === runId) {
          setResolutionError(
            apiErrorText(t, caught, 'inactivityCase.resolveFailedAfterBankId')
          );
          setResolutionStatus('failed');
        }
      }
      return true;
    }
    if (session.status === 'cancelled') {
      setResolutionStatus('cancelled');
      return true;
    }
    if (session.status === 'failed' || session.status === 'expired') {
      setResolutionError(session.message || t('inactivityCase.bankIdVerificationFailed'));
      setResolutionStatus('failed');
      return true;
    }
    return false;
  };

  const startResolution = async () => {
    if (!active) return;
    const runId = ++resolutionRunRef.current;
    setResolutionStatus('starting');
    setResolutionError(null);
    setBankIdSession(null);
    try {
      let session = await inactivityApi.startResolutionBankId(active.id);
      if (resolutionRunRef.current !== runId) return;
      setBankIdSession(session);
      setResolutionStatus('waiting');
      if (await handleSession(session, runId)) return;
      while (resolutionRunRef.current === runId) {
        await wait(session.pollIntervalMs ?? 1500);
        if (resolutionRunRef.current !== runId) return;
        session = session.provider === 'simulated'
          ? await inactivityApi.collectResolutionBankId(active.id, session.verificationRef)
          : await inactivityApi.getResolutionBankIdStatus(active.id, session.verificationRef);
        if (await handleSession(session, runId)) return;
      }
    } catch (caught: any) {
      if (resolutionRunRef.current !== runId) return;
      setResolutionError(
        apiErrorText(t, caught, 'inactivityCase.bankIdVerificationCouldNotComplete')
      );
      setResolutionStatus('failed');
    }
  };

  const cancelBankId = async () => {
    ++resolutionRunRef.current;
    if (active && bankIdSession?.verificationRef) {
      try {
        await inactivityApi.cancelResolutionBankId(
          active.id,
          bankIdSession.verificationRef
        );
      } catch {
        // Dialogen kan stängas även om leverantörens session löper ut senare.
      }
    }
    setResolutionStatus('cancelled');
  };

  const openResolution = () => {
    setResolutionStatus('idle');
    setResolutionError(null);
    setBankIdSession(null);
    setShowResolution(true);
  };

  if (active) {
    const completedSteps = active.steps.filter(step => step.status === 'completed').length;
    const reminderEscalated = completedSteps >= 2;
    return (
      <>
        {/* One notice (ClickUp 869fjrytd): the party who has to answer or may
            close the case gets an action, the other one waits. The first
            prompt is not repeated under its own title. */}
        <Notice
          level={inactivityViewerActs(active, viewerSide) ? 'action' : 'warning'}
          icon={canResolve ? 'shield' : isSilentViewer ? undefined : 'clock'}
          context={context}
          testId={`inactivity-case-${active.caseType}`}
          title={resolutionAvailable
            ? isSilentViewer
              ? t('inactivityCase.deadlineExpiredYou')
              : t('inactivityCase.deadlineExpired', { party: silentParty })
            : reminderEscalated
              ? isSilentViewer
                ? displayed.caseType === 'buyer_silence'
                  ? t('inactivityCase.stillWaitingYourAttestation')
                  : t('inactivityCase.stillWaitingYourResponse')
                : t('inactivityCase.noResponseStill', { party: silentParty })
              : isSilentViewer
                ? displayed.caseType === 'buyer_silence'
                  ? t('inactivityCase.checkAndAttest')
                  : t('inactivityCase.checkMilestoneAndRespond')
              : t('inactivityCase.waitingForParty', { party: silentParty })}
          action={canResolve
            ? { label: t('inactivityCase.resolveWithBankId'), onClick: openResolution }
            : isSilentViewer && !resolutionAvailable && onOpenMilestone
              ? { label: t('inactivityCase.openMilestone'), onClick: () => onOpenMilestone(milestone) }
              : undefined}
        >
          {(resolutionConfirmed || resolutionAvailable || reminderEscalated) && (
            <div className="small mt-1">
              {resolutionConfirmed
                ? t('inactivityCase.closureConfirmedBankId')
                : resolutionAvailable
                  ? canResolve
                    ? t('inactivityCase.youCanResolveNow')
                    : t('inactivityCase.counterpartyCanResolveNow')
                  : active.nextActionAt
                    ? t('inactivityCase.finalDeadlineAt', { date: dateTime(active.nextActionAt) })
                    : t('inactivityCase.finalDeadlinePreparing')}
            </div>
          )}
          {!(isSilentViewer && completedSteps === 0 && !resolutionAvailable) && (
            <div className="small text-muted mt-1">
              {resolutionAvailable
                ? isSilentViewer
                  ? null
                  : (viewerSide === 'person'
                    ? t('inactivityCase.ifYouResolveNoFee', { consequence: finalConsequence })
                    : t('inactivityCase.ifYouResolve', { consequence: finalConsequence }))
                : reminderEscalated
                  ? isSilentViewer
                    ? displayed.caseType === 'buyer_silence'
                      ? t('inactivityCase.ifYouDontAttest', { party: counterparty })
                      : t('inactivityCase.ifYouDontRespond', { party: counterparty })
                    : t('inactivityCase.partyReminded', { party: capitalizedSilentParty })
                  : completedSteps >= 1
                    ? isSilentViewer
                      ? reminderPrompt
                      : t('inactivityCase.friendlyReminderSent', { party: silentParty, action: requestedAction })
                    : isSilentViewer
                      ? directPrompt
                      : t('inactivityCase.waitingForPartyAction', { party: silentParty, action: requestedAction })}
            </div>
          )}
        </Notice>

        {showResolution && (
          <>
            <div className="modal show d-block" tabIndex={-1} role="dialog" aria-modal="true">
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{t('inactivityCase.resolveAfterSilenceTitle')}</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label={t('inactivityCase.close')}
                    disabled={['starting', 'waiting', 'saving'].includes(resolutionStatus)}
                    onClick={() => setShowResolution(false)}
                  />
                </div>
                <div className="modal-body">
                  {resolutionStatus === 'idle' && (
                    <>
                      <p>
                        {t('inactivityCase.deadlineExpiredResolve', { party: silentParty })}
                      </p>
                      <div className="alert alert-warning small mb-0">
                        {viewerSide === 'person'
                          ? finalConsequence
                          : t('inactivityCase.consequenceFeesNotRefunded', { consequence: finalConsequence })}
                      </div>
                    </>
                  )}
                  {resolutionStatus === 'starting' && (
                    <div className="text-center py-4" role="status">
                      <div className="spinner-border text-primary mb-3" />
                      <h6>{t('inactivityCase.startingBankId')}</h6>
                    </div>
                  )}
                  {resolutionStatus === 'waiting' && (
                    <div className="text-center py-2" role="status">
                      {bankIdQrImage ? (
                        <div className="d-inline-flex bg-white border rounded p-2 mb-3">
                          <img src={bankIdQrImage} alt={t('inactivityCase.qrAltBankId')} width="192" height="192" />
                        </div>
                      ) : (
                        <div className="spinner-border text-primary mb-3" />
                      )}
                      <h6>{t('inactivityCase.waitingForBankId')}</h6>
                      <p className="text-muted">
                        {bankIdSession?.message || t('inactivityCase.openBankIdAppInstructions')}
                      </p>
                      {bankIdSession?.sameDeviceUrl && (
                        <a className="btn btn-primary btn-sm" href={bankIdSession.sameDeviceUrl}>
                          {t('inactivityCase.openBankId')}
                        </a>
                      )}
                    </div>
                  )}
                  {resolutionStatus === 'saving' && (
                    <div className="text-center py-4" role="status">
                      <div className="spinner-border text-danger mb-3" />
                      <h6>{t('inactivityCase.closingProjectSettling')}</h6>
                    </div>
                  )}
                  {resolutionStatus === 'success' && (
                    <div className="text-center py-4" role="status">
                      <i className="feather-check-circle text-success d-block mb-3" style={{ fontSize: '4rem' }} />
                      <h6>{t('inactivityCase.closureConfirmed')}</h6>
                      <p className="text-muted mb-0">
                        {t('inactivityCase.bankIdSignedSettling')}
                      </p>
                    </div>
                  )}
                  {resolutionStatus === 'cancelled' && (
                    <div className="text-center py-4"><h6>{t('inactivityCase.projectNotClosed')}</h6></div>
                  )}
                  {resolutionStatus === 'failed' && (
                    <div className="text-center py-4" role="alert">
                      <i className="feather-alert-circle text-danger d-block mb-3" style={{ fontSize: '3rem' }} />
                      <h6>{t('inactivityCase.projectCouldNotBeClosed')}</h6>
                      <p className="text-muted mb-0">{resolutionError}</p>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  {resolutionStatus === 'idle' && (
                    <>
                      <button className="btn btn-light" onClick={() => setShowResolution(false)}>{t('inactivityCase.cancel')}</button>
                      <button className="btn btn-danger" onClick={() => void startResolution()}>
                        {t('inactivityCase.confirmWithBankId')}
                      </button>
                    </>
                  )}
                  {['starting', 'waiting'].includes(resolutionStatus) && (
                    <button className="btn btn-outline-danger ms-auto" onClick={() => void cancelBankId()}>
                      {t('inactivityCase.cancelBankId')}
                    </button>
                  )}
                  {resolutionStatus === 'failed' && (
                    <button className="btn btn-danger ms-auto" onClick={() => void startResolution()}>{t('inactivityCase.tryAgain')}</button>
                  )}
                  {['success', 'cancelled'].includes(resolutionStatus) && (
                    <button className="btn btn-primary ms-auto" onClick={() => setShowResolution(false)}>{t('inactivityCase.close')}</button>
                  )}
                </div>
                </div>
              </div>
            </div>
            <div className="modal-backdrop show" />
          </>
        )}
      </>
    );
  }

  if (completed) {
    return (
      <div className="alert alert-secondary mt-3 mb-0" data-testid={`inactivity-case-${completed.caseType}-completed`}>
        <div className="fw-semibold">{t('inactivityCase.projectClosedAfterSilence', { party: silentParty })}</div>
        <div className="small mt-1">{viewerSide === 'person' ? finalConsequence : t('inactivityCase.consequenceFeesNotRefundedPast', { consequence: finalConsequence })}</div>
      </div>
    );
  }

  return null;
}
